import type {
  PreisToolItem,
  PreisToolOpenPriceChange,
  PreisToolPriceChangeStatus,
  PreisToolPriceField,
  PreisToolSapWritebackMode,
  PreisToolSuppliersResponse,
} from '@/api/preis-tool/preis-tool.types';
import type { DsComboboxGroup } from '@/modules/internal-v2/ds';

export const fmtEUR = (n: number | null | undefined) =>
  n == null || isNaN(n)
    ? '—'
    : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtPct = (n: number | null | undefined, digits = 1) =>
  n == null || isNaN(n)
    ? '—'
    : (n * 100).toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits }) + '%';

export const marginPct = (price: number | null | undefined, mek: number | null | undefined) =>
  price == null || mek == null || mek === 0 ? null : (price - mek) / mek;

/** "Niedrige Marge" while no other limit is stored (EOS-61). */
export const DEFAULT_LOW_MARGIN_PCT = 0.05;

/**
 * `ds-pill` class of a markup: `neg` below 0, `warn` from 0 to below
 * `lowMarginPct`, else `pos`. For sale prices pass the legend's stored limit —
 * the row colour (`row-states.ts`) uses this very function, so pill and row
 * always agree. The offer table's Δ MEK keeps the default.
 */
export const marginClass = (m: number | null, lowMarginPct = DEFAULT_LOW_MARGIN_PCT) =>
  m == null ? 'muted' : m < 0 ? 'neg' : m < lowMarginPct ? 'warn' : 'pos';

/** A legend limit as percent, up to two decimals: 0.125 → "12,5 %". */
export const fmtLimitPct = (share: number) =>
  `${(share * 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;

export const CHANNELS = [
  { key: 'standardPrice' as const, label: 'Standard', sub: 'SAP-Preisliste 01 / Hauptkanal' },
  { key: 'itscopePrice' as const, label: 'ITscope', sub: 'B2B-Marktplatz (itscope.com)' },
  { key: 'onlinerPrice' as const, label: 'Onliner', sub: 'Onliner-Projektkanal' },
];

export type PriceField = PreisToolPriceField;

export function channelLabel(k: string) {
  return { standardPrice: 'Standard', itscopePrice: 'ITscope', onlinerPrice: 'Onliner' }[k] ?? k;
}

export function computeBedarf(a: PreisToolItem) {
  const total = a.quantityOnStock - a.quantityOrderedByCustomers + a.quantityOrderedFromVendors;
  return a.minInventory > total ? a.minInventory - total : 0;
}

export const PRICE_FIELDS: PriceField[] = ['standardPrice', 'itscopePrice', 'onlinerPrice'];

/** Label and `ds-pill` colour class per transfer status of a price change. */
export const PRICE_CHANGE_STATUS: Record<PreisToolPriceChangeStatus, { label: string; cls: string }> = {
  PENDING: { label: 'offen', cls: 'warn' },
  TRANSFERRED: { label: 'übertragen', cls: 'pos' },
  FAILED: { label: 'fehlgeschlagen', cls: 'neg' },
  SUPERSEDED: { label: 'ersetzt', cls: 'muted' },
};

/** One-line explanation of a price that is saved in EOS but not in SAP. */
export function openChangeText(change: PreisToolOpenPriceChange) {
  if (change.status === 'FAILED') {
    return `Übertragung nach SAP fehlgeschlagen · gewünscht € ${fmtEUR(change.targetPrice)}${change.errorMessage ? ` · ${change.errorMessage}` : ''}`;
  }
  return `Gespeichert, noch nicht in SAP · SAP-Preis ${change.sapPrice == null ? '—' : `€ ${fmtEUR(change.sapPrice)}`}`;
}

/** `null` once the real SAP write-back is connected. */
export function writebackHint(mode: PreisToolSapWritebackMode | undefined) {
  if (mode === 'disabled') {
    return 'SAP-Übertragung ist noch nicht angebunden. Gespeicherte Preise bleiben offen und gelten nur im Preis-Tool.';
  }
  if (mode === 'simulate-success' || mode === 'simulate-failure') {
    return 'SAP-Übertragung wird nur simuliert (Entwicklung). In SAP kommt nichts an.';
  }
  return null;
}

export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** Reads a typed price ("440", "440,5", "1.234,50"); `null` when it is not a number. */
export function parsePriceInput(input: string): number | null {
  const cleaned = input.trim().replace(/\s/g, '');
  if (cleaned === '') return null;
  const normalised = cleaned.includes(',') ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned;
  const n = Number(normalised);
  return Number.isFinite(n) ? +n.toFixed(2) : null;
}

/**
 * Pick list of the supplier fields (fixed price, manual offer, price list
 * upload): the supplier business partners from SAP with their number, then
 * the names typed in so far. The value is the name; the dialogs link a name
 * of the first group to its business partner when saving.
 */
export function supplierGroups(data: PreisToolSuppliersResponse | undefined): DsComboboxGroup[] {
  return [
    {
      label: 'Aus SAP',
      options: (data?.suppliers ?? []).map((s) => ({ value: s.name, label: s.name, hint: String(s.id) })),
    },
    {
      label: 'Bisher frei eingetippt',
      options: (data?.typedNames ?? []).map((name) => ({ value: name, label: name })),
    },
  ];
}

/** Last entry of a supplier field while the typed name is new. */
export const newSupplierLabel = (name: string) => `„${name}“ als neuen Lieferanten verwenden`;

/**
 * Upstream suppliers of a fixed price (EOS-58), shown until the central
 * Vorlieferanten of EOS-220 replace them. Set it to `false` to hide them in
 * the fixed price dialog and the offer table; the stored data and the API stay.
 */
export const UPSTREAM_SUPPLIERS_ENABLED = true;

/**
 * Stock of a fixed price or a manual offer: what the supplier has itself
 * (`own`, from its price list or typed in) plus the known stock of its
 * upstream suppliers (fixed prices only, while they are shown). `total` is
 * `null` while neither is known; `title` splits it up for the tooltip.
 */
export function offerStockSummary(
  own: number | null,
  upstream: { name: string; stockQty: number | null }[] | null,
) {
  const sources = UPSTREAM_SUPPLIERS_ENABLED ? (upstream ?? []) : [];
  const known = sources.filter((u) => u.stockQty != null);
  const upstreamSum = known.reduce((total, u) => total + (u.stockQty ?? 0), 0);
  const total = own == null && known.length === 0 ? null : (own ?? 0) + upstreamSum;
  const pieces = (n: number | null) => (n == null ? 'unbekannt' : `${n.toLocaleString('de-DE')} Stk`);
  const lines = [`Lieferant selbst: ${pieces(own)}`];
  if (upstream && UPSTREAM_SUPPLIERS_ENABLED) {
    lines.push(
      sources.length === 0
        ? 'Vorlieferanten: keine hinterlegt'
        : `Vorlieferanten: ${sources.map((u) => `${u.name} ${pieces(u.stockQty)}`).join(', ')}`,
    );
    if (total != null) lines.push(`Zusammen: ${pieces(total)}`);
  }
  return { total, title: lines.join('\n') };
}

/**
 * Stock of a fixed price as recorded at its upstream suppliers: short text for
 * the cell plus the single sources for the tooltip. `null` stock = unknown.
 */
export function upstreamStockSummary(upstream: { name: string; stockQty: number | null }[]) {
  if (upstream.length === 0) {
    return { text: 'keine Lagerquelle', title: 'Für diesen Fixpreis ist keine Lagerquelle hinterlegt.', known: false };
  }
  const known = upstream.filter((u) => u.stockQty != null);
  const sum = known.reduce((total, u) => total + (u.stockQty ?? 0), 0);
  return {
    text: known.length === 0 ? 'Bestand unbekannt' : `Σ ${sum.toLocaleString('de-DE')} Stk`,
    title: upstream
      .map((u) => `${u.name}: ${u.stockQty == null ? 'unbekannt' : `${u.stockQty.toLocaleString('de-DE')} Stk`}`)
      .join(' · '),
    known: known.length > 0,
  };
}
